import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Upload, Users, Server, Sparkles, Palette } from "lucide-react";

const STEPS = [
  { key: "welcome", label: "Welcome", icon: Sparkles },
  { key: "logo", label: "Logo & brand", icon: Upload },
  { key: "advisors", label: "Advisor roster", icon: Users },
  { key: "brand", label: "Brand color", icon: Palette },
  { key: "dms", label: "DMS credentials", icon: Server },
];

/**
 * First-run wizard shown after a new dealer completes Stripe checkout.
 * Route: /first-run. Mounted from LoginPage's post-login redirect when
 * `user.onboarding_completed === false`. Distinct from the sales-rep
 * `/onboarding` checklist page which is a static contract-signing playbook.
 */
export default function FirstRunWizardPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [settings, setSettings] = useState(null);
  const [advisors, setAdvisors] = useState([{ name: "", phone: "", email: "" }]);
  const [brandColor, setBrandColor] = useState("#0f172a");
  const [dms, setDms] = useState({ dms_type: "", dms_host: "", dms_username: "", dms_notes: "" });

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      if (r.data?.brand_primary_color) setBrandColor(r.data.brand_primary_color);
    }).catch(() => {});
  }, []);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const saveBrandColor = async () => {
    try {
      await api.put("/settings", { brand_primary_color: brandColor });
      toast.success("Brand color saved");
      next();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const saveLogo = async () => {
    try {
      await api.put("/settings", { dealership_logo_url: settings?.dealership_logo_url || "" });
      toast.success("Logo saved");
      next();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const saveAdvisors = async () => {
    const filled = advisors.filter((a) => a.name.trim());
    for (const a of filled) {
      try {
        await api.post("/advisors", { name: a.name.trim(), phone: a.phone.trim(), email: a.email.trim() });
      } catch (_) { /* duplicates are OK */ }
    }
    toast.success(`${filled.length || "No"} advisor${filled.length === 1 ? "" : "s"} added`);
    next();
  };

  const saveDms = async () => {
    try {
      await api.put("/settings", { dms_type: dms.dms_type, dms_host: dms.dms_host, dms_username: dms.dms_username, dms_notes: dms.dms_notes });
      await api.post("/tenants/onboarding/complete");
      toast.success("You're all set — welcome to Decline Drive");
      navigate("/dashboard");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const skipAll = async () => {
    try { await api.post("/tenants/onboarding/complete"); } catch (_) {}
    navigate("/dashboard");
  };

  return (
    <DashboardShell>
      <div className="p-6 max-w-3xl mx-auto" data-testid="first-run-page">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-[hsl(var(--gold))]" strokeWidth={2} />
          <div className="font-mono-label text-[hsl(var(--gold))] text-[0.65rem] tracking-widest">FIRST-RUN SETUP</div>
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-navy mb-2" data-testid="first-run-heading">Let's get your dashboard ready</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Four quick steps and you're pitching. Skip any step and revisit from Settings later.
        </p>

        <div className="flex items-center gap-2 mb-6" data-testid="first-run-steps">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < step;
            const active = i === step;
            return (
              <div key={s.key} className="flex items-center gap-2 flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${done ? "bg-emerald-500 text-white" : active ? "bg-navy text-white ring-4 ring-navy/20" : "bg-muted text-muted-foreground"}`}>
                  {done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <div className={`text-xs font-mono-label ${active ? "text-navy font-bold" : "text-muted-foreground"}`}>{s.label}</div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${done ? "bg-emerald-500" : "bg-muted"}`} />}
              </div>
            );
          })}
        </div>

        {step === 0 && (
          <Card className="p-8" data-testid="first-run-step-welcome">
            <h2 className="font-display text-3xl font-bold text-navy mb-3">Welcome aboard</h2>
            <p className="text-sm text-foreground/80 mb-6 leading-relaxed">
              You're 3 steps away from a live dashboard: your logo, your advisor roster, and your DMS credentials.
              Whole thing takes under 5 minutes. On a live pitch? Hit <em>Skip everything</em> — we'll set defaults.
            </p>
            <div className="flex gap-3">
              <Button onClick={next} className="bg-navy hover:bg-navy/85 group" data-testid="first-run-start-btn">
                Let's go <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
              </Button>
              <Button variant="outline" onClick={skipAll} data-testid="first-run-skip-all-btn">Skip everything</Button>
            </div>
          </Card>
        )}

        {step === 1 && (
          <Card className="p-8" data-testid="first-run-step-logo">
            <h2 className="font-display text-2xl font-bold text-navy mb-1">Your dealership brand</h2>
            <p className="text-sm text-muted-foreground mb-6">Paste your logo URL (transparent PNG preferred). Appears on the sidebar, contracts, and every SMS coupon.</p>
            <div className="mb-4">
              <Label htmlFor="logo_url" className="font-mono-label">Logo URL</Label>
              <Input id="logo_url" value={settings?.dealership_logo_url || ""} onChange={(e) => setSettings({ ...settings, dealership_logo_url: e.target.value })} placeholder="https://your-dealership.com/logo.png" className="mt-1.5" data-testid="first-run-logo-input" />
            </div>
            {settings?.dealership_logo_url && (
              <div className="border border-border rounded-lg p-4 mb-4 bg-muted/30">
                <div className="font-mono-label text-[0.65rem] tracking-widest text-muted-foreground mb-2">PREVIEW</div>
                <img src={settings.dealership_logo_url} alt="Logo preview" className="max-h-20 max-w-[240px] object-contain" onError={(e) => (e.target.style.display = "none")} />
              </div>
            )}
            <div className="flex gap-3 mt-6">
              <Button onClick={saveLogo} className="bg-navy hover:bg-navy/85" data-testid="first-run-logo-save-btn">Save & continue</Button>
              <Button variant="outline" onClick={next} data-testid="first-run-logo-skip-btn">Skip</Button>
            </div>
          </Card>
        )}

        {step === 2 && (
          <Card className="p-8" data-testid="first-run-step-advisors">
            <h2 className="font-display text-2xl font-bold text-navy mb-1">Your advisor roster</h2>
            <p className="text-sm text-muted-foreground mb-6">Add every advisor now — you can always add more later. Name is required, phone + email optional.</p>
            <div className="space-y-3">
              {advisors.map((a, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid={`first-run-advisor-row-${i}`}>
                  <Input placeholder="Name" value={a.name} onChange={(e) => setAdvisors(advisors.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                  <Input placeholder="Phone" value={a.phone} onChange={(e) => setAdvisors(advisors.map((x, j) => j === i ? { ...x, phone: e.target.value } : x))} />
                  <Input placeholder="Email" value={a.email} onChange={(e) => setAdvisors(advisors.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} />
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setAdvisors([...advisors, { name: "", phone: "", email: "" }])} data-testid="first-run-advisor-add-btn">
                + Add another
              </Button>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={saveAdvisors} className="bg-navy hover:bg-navy/85" data-testid="first-run-advisors-save-btn">Save & continue</Button>
              <Button variant="outline" onClick={next} data-testid="first-run-advisors-skip-btn">Skip</Button>
            </div>
          </Card>
        )}

        {step === 3 && (
          <Card className="p-8" data-testid="first-run-step-brand">
            <h2 className="font-display text-2xl font-bold text-navy mb-1">Pick your brand color</h2>
            <p className="text-sm text-muted-foreground mb-6">This becomes the accent color on your sidebar, buttons, and email templates — makes every screen feel like yours.</p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="brand_hex" className="font-mono-label">Hex color</Label>
                <div className="flex items-center gap-3 mt-1.5">
                  <input id="brand_hex" type="color" value={brandColor} onChange={(e) => setBrandColor(e.target.value)} className="w-16 h-11 rounded-md border border-border cursor-pointer" data-testid="first-run-brand-color-picker" />
                  <Input value={brandColor} onChange={(e) => setBrandColor(e.target.value)} placeholder="#0f172a" className="font-mono" data-testid="first-run-brand-color-input" />
                </div>
                <div className="mt-4 grid grid-cols-6 gap-2">
                  {["#0f172a", "#dc2626", "#ea580c", "#059669", "#0284c7", "#7c3aed"].map((c) => (
                    <button key={c} type="button" onClick={() => setBrandColor(c)} className="h-8 rounded-md border border-border" style={{ backgroundColor: c }} title={c} data-testid={`first-run-brand-swatch-${c.slice(1)}`} />
                  ))}
                </div>
              </div>
              <div>
                <div className="font-mono-label text-[0.65rem] tracking-widest text-muted-foreground mb-2">LIVE PREVIEW</div>
                <div className="rounded-lg border border-border overflow-hidden">
                  <div className="p-4 text-white font-display font-bold" style={{ backgroundColor: brandColor }}>Your dealership header</div>
                  <div className="p-4 bg-background">
                    <button type="button" className="px-4 py-2 rounded-md text-white font-semibold text-sm" style={{ backgroundColor: brandColor }}>Primary button</button>
                    <div className="mt-3 text-sm font-medium" style={{ color: brandColor }}>Accent link · same color</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={saveBrandColor} className="bg-navy hover:bg-navy/85" data-testid="first-run-brand-save-btn">Save & continue</Button>
              <Button variant="outline" onClick={next} data-testid="first-run-brand-skip-btn">Skip</Button>
            </div>
          </Card>
        )}

        {step === 4 && (
          <Card className="p-8" data-testid="first-run-step-dms">
            <h2 className="font-display text-2xl font-bold text-navy mb-1">DMS credentials</h2>
            <p className="text-sm text-muted-foreground mb-6">Which DMS are you on? We use these to pull last night's declined ROs. Not ready to hand over creds? Drop a note — we'll follow up.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dms_type" className="font-mono-label">DMS platform</Label>
                <Input id="dms_type" placeholder="CDK / Reynolds / Dealertrack" value={dms.dms_type} onChange={(e) => setDms({ ...dms, dms_type: e.target.value })} className="mt-1.5" data-testid="first-run-dms-type" />
              </div>
              <div>
                <Label htmlFor="dms_host" className="font-mono-label">SFTP / API host</Label>
                <Input id="dms_host" placeholder="sftp.dealer.example.com" value={dms.dms_host} onChange={(e) => setDms({ ...dms, dms_host: e.target.value })} className="mt-1.5" data-testid="first-run-dms-host" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="dms_username" className="font-mono-label">Username</Label>
                <Input id="dms_username" value={dms.dms_username} onChange={(e) => setDms({ ...dms, dms_username: e.target.value })} className="mt-1.5" data-testid="first-run-dms-username" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="dms_notes" className="font-mono-label">Notes for our team</Label>
                <Textarea id="dms_notes" placeholder="Anything we should know? Who signs the DPA?" value={dms.dms_notes} onChange={(e) => setDms({ ...dms, dms_notes: e.target.value })} rows={3} className="mt-1.5" data-testid="first-run-dms-notes" />
              </div>
            </div>
            <p className="text-[0.7rem] text-muted-foreground mt-3">
              💡 We store host + username here — never a password. When you're ready, we'll do a secure key exchange for the actual SFTP credential.
            </p>
            <div className="flex gap-3 mt-6">
              <Button onClick={saveDms} className="bg-navy hover:bg-navy/85 group" data-testid="first-run-finish-btn">
                Finish setup <CheckCircle2 className="w-4 h-4 ml-2 group-hover:scale-110 transition-transform" />
              </Button>
              <Button variant="outline" onClick={skipAll} data-testid="first-run-dms-skip-btn">Skip for now</Button>
            </div>
          </Card>
        )}

        {step > 0 && step < STEPS.length && (
          <div className="mt-4">
            <button onClick={back} className="text-xs text-muted-foreground hover:text-foreground underline" data-testid="first-run-back-btn">← Back</button>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
